import { NavLink, generatePath, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { Button, PageBreadcrumbs, PageNavigation, PageDetailHeader, Skeleton } from '@client/toolkit';
import { PermissionKeys, useGetAgreementsByIdQuery } from '@client/api';
import { AgreementsPaths } from '../../agreements.router';
import { PageContentAnimation } from '../../../../components/layout/page-content-animation';
import { BackButton } from '../../../../components';
import { AgreementDeleteModal } from './agreement-delete-modal.component';
import { AgreementPermissionBasedActionButton } from '../../../../components/agreement-permission-based-button/agreement-permission-based-button';

export const AgreementHeader = () => {
  const { t, ready } = useTranslation('agreements');
  const { t: tCommon } = useTranslation();

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [deleting, setDeleting] = useState<boolean>(false);
  const { data: agreement, isFetching } = useGetAgreementsByIdQuery({ id: id ?? '' }, { skip: id == null });

  const isAgreementActive = agreement?.status === 'Effective';

  const tabs = useMemo(() => {
    if (!id) return [];
    return [
      {
        key: 'details',
        label: t('agreement.tabs.details'),
        to: generatePath(AgreementsPaths.Agreement, { id }),
        end: true,
      },
      {
        key: 'parties',
        label: t('agreement.tabs.parties'),
        to: generatePath(AgreementsPaths.AgreementParties, { id }),
        end: false,
      },
      {
        key: 'documents',
        label: t('agreement.tabs.documents'),
        to: generatePath(AgreementsPaths.AgreementDocuments, { id }),
        end: false,
      },
      {
        key: 'invoicing',
        label: t('agreement.tabs.invoicing'),
        to: generatePath(AgreementsPaths.AgreementInvoicing, { id }),
        end: false,
      },
      {
        key: 'auditTrail',
        label: t('agreement.tabs.auditTrail'),
        to: generatePath(AgreementsPaths.AgreementAuditTrail, { id }),
        end: false,
      },
    ];
  }, [t, id]);

  const handleClickAgreementListLink = () => {
    navigate(generatePath(AgreementsPaths.AgreementList));
  };

  const handleOpenDelete = () => {
    setDeleting(true);
  };

  const handleCloseDelete = () => {
    setDeleting(false);
  };

  const handleDeleteFulfilled = () => {
    setDeleting(false);
    navigate(generatePath(AgreementsPaths.AgreementList));
  };

  return (
    <>
      <PageNavigation className="px-4">
        <PageBreadcrumbs className="py-4">
          <PageBreadcrumbs.Link onClick={handleClickAgreementListLink}>
            {ready ? t('overview') : <Skeleton variant="text" width="w-24" />}
          </PageBreadcrumbs.Link>
          <PageBreadcrumbs.Link>
            {ready ? t('agreement.title') : <Skeleton variant="text" width="w-24" />}
          </PageBreadcrumbs.Link>
        </PageBreadcrumbs>
      </PageNavigation>

      <PageDetailHeader>
        <PageContentAnimation>
          <div className="flex items-center">
            <div>
              <BackButton />
            </div>
            <div className="flex-grow" />
            <div className="flex space-x-2 items-center ml-4">
              <AgreementPermissionBasedActionButton
                agreementId={id ?? ''}
                permissions={[PermissionKeys.Agreement.Delete]}
                onClick={handleOpenDelete}
                disabled={isFetching || id == null || isAgreementActive}
                disabledTooltip={isAgreementActive ? t('activeAgreementDeleteDisabled') : undefined}
                title={tCommon('delete')}
                color="danger"
              />
            </div>
          </div>
        </PageContentAnimation>
      </PageDetailHeader>

      <PageContentAnimation className="px-5 -mt-10 mb-2.5">
        <div className="flex gap-8 border-b border-border w-full">
          {tabs.map((tab) => (
            <NavLink
              key={tab.key}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) =>
                classNames(
                  'pb-2 text-sm font-medium transition-colors',
                  tab.key === 'auditTrail' && 'ml-auto',
                  isActive
                    ? 'text-brand-primary border-b-2 border-brand-primary'
                    : 'text-white hover:text-brand-primary',
                )
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </div>
      </PageContentAnimation>

      {agreement?.id != null && !isFetching && (
        <AgreementDeleteModal
          agreement={agreement}
          open={deleting}
          onCancel={handleCloseDelete}
          onDeleteFulfilled={handleDeleteFulfilled}
        />
      )}
    </>
  );
};
